import dynamic from "next/dynamic";
import { getComponentName } from "@/utils/componentMapper";

const normalizeBannerCustomization = (customization = {}) => {
  const normalized = { ...customization };
  const prefixBreakpointClass = (prefix, value) => {
    if (!value || typeof value !== "string") return "";

    return value
      .split(/\s+/)
      .filter(Boolean)
      .map((className) =>
        className.includes(":") ? className : `${prefix}:${className}`,
      )
      .join(" ");
  };
  const normalizeResponsiveFontSize = (targetKey, smKey, mdKey, lgKey) => {
    if (normalized[targetKey]) return;

    const classes = [
      normalized[smKey] || "",
      prefixBreakpointClass("md", normalized[mdKey]),
      prefixBreakpointClass("lg", normalized[lgKey]),
    ]
      .filter(Boolean)
      .join(" ");

    if (classes) {
      normalized[targetKey] = classes;
    }
  };

  normalizeResponsiveFontSize(
    "subtitle_font_size",
    "subtitle_font_size_sm",
    "subtitle_font_size_md",
    "subtitle_font_size_lg",
  );
  normalizeResponsiveFontSize(
    "title_font_size",
    "title_font_size_sm",
    "title_font_size_md",
    "title_font_size_lg",
  );
  normalizeResponsiveFontSize(
    "description_font_size",
    "description_font_size_sm",
    "description_font_size_md",
    "description_font_size_lg",
  );

  const overlayColor =
    normalized.image_overlay_color || normalized.overlay_background_color;
  if (overlayColor && !normalized.overlay_background_color) {
    normalized.overlay_background_color = overlayColor;
  }

  if (!normalized.content_alignment) {
    const horizontal = normalized.content_alignment_horizontal || "start";
    const horizontalClassMap = {
      start: "items-start",
      left: "items-start",
      center: "items-center",
      end: "items-end",
      right: "items-end",
    };
    normalized.content_alignment =
      horizontalClassMap[horizontal] || "items-start";
  }

  if (!normalized.text_alignment) {
    const horizontal = normalized.content_alignment_horizontal || "start";
    const textClassMap = {
      start: "text-start",
      left: "text-start",
      center: "text-center",
      end: "text-end",
      right: "text-end",
    };
    normalized.text_alignment = textClassMap[horizontal] || "text-start";
  }

  if (!normalized.content_justify) {
    const vertical = normalized.content_alignment_vertical || "center";
    const verticalClassMap = {
      top: "justify-start",
      start: "justify-start",
      center: "justify-center",
      middle: "justify-center",
      bottom: "justify-end",
      end: "justify-end",
    };
    normalized.content_justify =
      verticalClassMap[vertical] || "justify-center";
  }

  return normalized;
};

const normalizeComponentConfig = (componentConfig) => {
  if (!componentConfig || !componentConfig.component_type) return componentConfig;
  let customization = componentConfig.customization || {};

  if (componentConfig.component_type === "banner") {
    customization = normalizeBannerCustomization(customization);
  }

  // Backward-compatible bridge:
  // banyak varian lama membaca key langsung dari componentConfig, bukan customization.
  // Merge agar key builder baru tetap terbaca tanpa migrasi data.
  return {
    ...componentConfig,
    ...customization,
    customization,
  };
};

const getWrapperProps = (componentConfig) => {
  const customization = componentConfig?.customization || {};
  switch (componentConfig?.component_type) {
    case "footer":
      return {
        className: [
          customization.section_background_color || "",
          customization.section_padding || "",
        ]
          .filter(Boolean)
          .join(" "),
      };
    case "testimonial":
      return {
        className: [
          customization.card_background_color || "",
          customization.layout_gap_x || "",
          customization.layout_gap_y || "",
        ]
          .filter(Boolean)
          .join(" "),
      };
    default:
      return null;
  }
};

/**
 * Component Import Mapping
 * Mapping semua kemungkinan component imports sebagai string literal
 * untuk memungkinkan Next.js resolve imports saat build time
 */
const COMPONENT_IMPORTS = {
  // Header components
  HVariantOne: () => import("@/components/header/HVariantOne"),
  HVariantTwo: () => import("@/components/header/HVariantTwo"),
  HVariantThree: () => import("@/components/header/HVariantThree"),
  HVariantFour: () => import("@/components/header/HVariantFour"),
  HVariantFive: () => import("@/components/header/HVariantFive"),

  // Banner components
  BVariantOne: () => import("@/components/banner/BVariantOne"),
  BVariantTwo: () => import("@/components/banner/BVariantTwo"),
  BVariantThree: () => import("@/components/banner/BVariantThree"),
  BVariantFour: () => import("@/components/banner/BVariantFour"),
  BVariantFive: () => import("@/components/banner/BVariantFive"),
  BVariantSix: () => import("@/components/banner/BVariantSix"),

  // Category components
  CVariantOne: () => import("@/components/category/CVariantOne"),
  CVariantTwo: () => import("@/components/category/CVariantTwo"),
  CVariantThree: () => import("@/components/category/CVariantThree"),
  CVariantFour: () => import("@/components/category/CVariantFour"),
  CVariantFive: () => import("@/components/category/CVariantFive"),
  CVariantSix: () => import("@/components/category/CVariantSix"),
  CVariantSeven: () => import("@/components/category/CVariantSeven"),
  CVariantEight: () => import("@/components/category/CVariantEight"),

  // Product Category components
  PCVariantOne: () => import("@/components/product-category/PCVariantOne"),
  PCVariantTwo: () => import("@/components/product-category/PCVariantTwo"),
  PCVariantThree: () => import("@/components/product-category/PCVariantThree"),
  PCVariantFour: () => import("@/components/product-category/PCVariantFour"),
  PCVariantFive: () => import("@/components/product-category/PCVariantFive"),
  PCVariantSix: () => import("@/components/product-category/PCVariantSix"),
  PCVariantSeven: () => import("@/components/product-category/PCVariantSeven"),
  PCVariantEight: () => import("@/components/product-category/PCVariantEight"),

  // Product List components
  PLVariantOne: () => import("@/components/product-list/PLVariantOne"),
  PLVariantTwo: () => import("@/components/product-list/PLVariantTwo"),
  PLVariantThree: () => import("@/components/product-list/PLVariantThree"),
  PLVariantFour: () => import("@/components/product-list/PLVariantFour"),
  PLVariantFive: () => import("@/components/product-list/PLVariantFive"),
  PLVariantSix: () => import("@/components/product-list/PLVariantSix"),
  PLVariantSeven: () => import("@/components/product-list/PLVariantSeven"),

  // Testimonial components
  TVariantOne: () => import("@/components/testimonial/TVariantOne"),
  TVariantTwo: () => import("@/components/testimonial/TVariantTwo"),
  TVariantThree: () => import("@/components/testimonial/TVariantThree"),
  TVariantFour: () => import("@/components/testimonial/TVariantFour"),
  TVariantFive: () => import("@/components/testimonial/TVariantFive"),

  // Feature components
  FsVariantOne: () => import("@/components/features/FsVariantOne"),
  FsVariantTwo: () => import("@/components/features/FsVariantTwo"),
  FsVariantThree: () => import("@/components/features/FsVariantThree"),
  FsVariantFour: () => import("@/components/features/FsVariantFour"),
  FsVariantFive: () => import("@/components/features/FsVariantFive"),
  FsVariantSix: () => import("@/components/features/FsVariantSix"),
  FsVariantSeven: () => import("@/components/features/FsVariantSeven"),

  // Brand components
  BrVariantOne: () => import("@/components/brand/BrVariantOne"),
  BrVariantTwo: () => import("@/components/brand/BrVariantTwo"),
  BrVariantThree: () => import("@/components/brand/BrVariantThree"),
  BrVariantFour: () => import("@/components/brand/BrVariantFour"),
  BrVariantFive: () => import("@/components/brand/BrVariantFive"),
  BrVariantSix: () => import("@/components/brand/BrVariantSix"),

  // Blog components
  BlVariantOne: () => import("@/components/blog/BlVariantOne"),
  BlVariantTwo: () => import("@/components/blog/BlVariantTwo"),

  // CTA components
  CTAOne: () => import("@/components/cta/CTAOne"),
  CTATwo: () => import("@/components/cta/CTATwo"),
  CTAThree: () => import("@/components/cta/CTAThree"),
  CTAFour: () => import("@/components/cta/CTAFour"),

  // Footer components
  FVariantOne: () => import("@/components/footer/FVariantOne"),
  FVariantTwo: () => import("@/components/footer/FVariantTwo"),
  FVariantThree: () => import("@/components/footer/FVariantThree"),
  FVariantFour: () => import("@/components/footer/FVariantFour"),
  FVariantFive: () => import("@/components/footer/FVariantFive"),
  FVariantSix: () => import("@/components/footer/FVariantSix"),

  // Collection components (untuk product-showcase fallback)
  ColVariantOne: () => import("@/components/collection/ColVariantOne"),
  ColVariantTwo: () => import("@/components/collection/ColVariantTwo"),
  ColVariantThree: () => import("@/components/collection/ColVariantThree"),
  ColVariantFour: () => import("@/components/collection/ColVariantFour"),
  ColVariantFive: () => import("@/components/collection/ColVariantFive"),

  // Landing Content components
  LCVariantOne: () => import("@/components/landing-content/LCVariantOne"),
  LCVariantTwo: () => import("@/components/landing-content/LCVariantTwo"),
};

function getLoadingPlaceholderClass(componentType = "") {
  switch (componentType) {
    case "header":
      return "h-24";
    case "banner":
      return "h-[420px] md:h-[600px]";
    case "category":
    case "brand":
    case "feature":
      return "h-64";
    case "product-list":
    case "product-category":
      // Gunakan tinggi yang lebih mendekati konten nyata di mobile
      // untuk mengurangi layout shift saat skeleton diganti komponen asli
      return "h-[420px] md:h-[520px]";
    case "collection":
    case "product-showcase":
      return "h-[360px]";
    case "blog":
      return "h-[420px]";
    case "cta":
      return "h-48";
    case "footer":
      return "h-72";
    default:
      return "h-64";
  }
}

/**
 * Dynamic Component Loader
 * Load component secara dynamic berdasarkan template_key dari config
 */
export const loadDynamicComponent = (componentConfig) => {
  if (!componentConfig || !componentConfig.template_key) {
    return null;
  }

  const componentName = getComponentName(componentConfig.template_key);

  if (!componentName) {
    console.warn(
      `[DynamicComponentLoader] Component name tidak ditemukan untuk template_key: ${componentConfig.template_key}`
    );
    return null;
  }

  // Cek apakah component ada di mapping
  const componentImport = COMPONENT_IMPORTS[componentName];
  if (!componentImport) {
    console.warn(
      `[DynamicComponentLoader] Component import tidak ditemukan untuk: ${componentName}`
    );
    return null;
  }

  try {
    const loadingClass = getLoadingPlaceholderClass(
      componentConfig?.component_type,
    );

    // Load component secara dynamic menggunakan mapping
    const DynamicComponent = dynamic(
      () => {
        return componentImport()
          .then((module) => {
            // Handle default export
            return module.default || module;
          })
          .catch((err) => {
            console.error(
              `[DynamicComponentLoader] Error loading component ${componentName}:`,
              err
            );
            // Return fallback component jika error
            return () => (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  Component tidak dapat dimuat: {componentName}
                </p>
              </div>
            );
          });
      },
      {
        ssr: false, // Disable SSR untuk dynamic components
        loading: () => (
          <div
            className={`animate-pulse bg-gray-100 rounded-lg w-full ${loadingClass}`}
          />
        ),
      }
    );

    return DynamicComponent;
  } catch (error) {
    console.error(
      `[DynamicComponentLoader] Error creating dynamic component:`,
      error
    );
    return null;
  }
};

/**
 * Render component berdasarkan config
 */
export const DynamicComponent = ({ componentConfig, ...props }) => {
  const normalizedComponentConfig = normalizeComponentConfig(componentConfig);
  const Component = loadDynamicComponent(normalizedComponentConfig);

  if (!Component) {
    return null;
  }

  const wrapperProps = getWrapperProps(normalizedComponentConfig);
  const content = (
    <Component {...props} componentConfig={normalizedComponentConfig} />
  );
  if (!wrapperProps?.className) return content;

  return <div className={wrapperProps.className}>{content}</div>;
};

export default DynamicComponent;
